import { useCallback, useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { downloadGraphFileApi } from "@/api/api"
import { CheckSquare, Info, Trash2, Upload, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { graphDownloadErrorMessage } from "@/features/files/graph-download-error-message"
import { useBulkDeleteProjectFiles } from "@/features/projects/hooks/use-bulk-delete-project-files"
import {
  getProjectFileStatusSummary,
  isProjectFileEffectivelyPending,
} from "@/features/projects/utils/project-file-status"
import {
  useProjectEmbeddingStatus,
  useProjectFiles,
  useUploadProjectFiles,
} from "@/hooks/use-projects"
import { queryKeys } from "@/lib/query-keys"
import { SettingsService } from "@/services/settings-service"
import { useProjectFileSelectionStore } from "@/stores/project-file-selection-store"

import { ProjectFileList } from "./project-file-list"

type ProjectFileSettingsProps = {
  projectId: string
  canEdit?: boolean
}

export function ProjectFileSettings({ projectId, canEdit = true }: ProjectFileSettingsProps) {
  const queryClient = useQueryClient()
  const { t } = useTranslation("", { keyPrefix: "project_settings.files" })
  const [filePickerOpen, setFilePickerOpen] = useState(false)
  const [settingsService] = useState<SettingsService>(SettingsService.getInstance())
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const isBulkDeletingRef = useRef(false)

  const {
    data: files,
    isPending: isListingFiles,
    isReindexPolling,
    isUploadRefreshPending,
  } = useProjectFiles(projectId, canEdit)

  // File selection store
  const selectedFiles = useProjectFileSelectionStore((state) => state.selectedFiles)
  const selectAll = useProjectFileSelectionStore((state) => state.selectAll)
  const deselectAll = useProjectFileSelectionStore((state) => state.deselectAll)
  const deletionProgress = useProjectFileSelectionStore((state) => state.deletionProgress)
  const resetSelection = useProjectFileSelectionStore((state) => state.reset)

  // Bulk delete hook
  const bulkDelete = useBulkDeleteProjectFiles()
  const isBulkDeleting = bulkDelete.isPending
  isBulkDeletingRef.current = isBulkDeleting

  const handleExitSelectionMode = useCallback(() => {
    setIsSelectionMode(false)
    deselectAll()
  }, [deselectAll])

  const handleEnterSelectionMode = useCallback((filename?: string, index?: number) => {
    setIsSelectionMode(true)
    if (filename) {
      const { toggleFile } = useProjectFileSelectionStore.getState()
      toggleFile(filename, index)
    }
  }, [])

  // Reset selection when projectId changes
  useEffect(() => {
    return () => {
      if (!isBulkDeletingRef.current) {
        resetSelection()
        setIsSelectionMode(false)
      }
    }
  }, [projectId, resetSelection])

  const localStatus = getProjectFileStatusSummary(files)
  const { data: embeddingStatus } = useProjectEmbeddingStatus(
    projectId,
    isUploadRefreshPending || localStatus.pendingFileCount > 0
  )
  // A disabled TanStack query can retain an older polling response. Once the
  // fresh file list is terminal, ignore that cache so it cannot resurrect a
  // stale pending state.
  const activeEmbeddingStatus = localStatus.pendingFileCount > 0 ? embeddingStatus : undefined
  const status = getProjectFileStatusSummary(files, activeEmbeddingStatus)
  const hasPendingFiles = status.pendingFileCount > 0

  // Get selectable filenames (exclude files that are effectively still indexing).
  // A provider-ready file remains selectable even if its legacy shadow is stale.
  const selectableFilenamesList =
    files
      ?.filter((file) => !isProjectFileEffectivelyPending(file, activeEmbeddingStatus))
      .map((file) => file.filename) ?? []
  const selectableFilenamesSet = new Set(selectableFilenamesList)

  const selectedCount = selectedFiles.size
  const allSelected =
    selectableFilenamesList.length > 0 && selectedCount === selectableFilenamesList.length

  const handleSelectAll = () => {
    if (allSelected) {
      deselectAll()
    } else {
      selectAll(selectableFilenamesList)
    }
  }

  const handleBulkDelete = () => {
    const currentSelectedFiles = useProjectFileSelectionStore.getState().selectedFiles
    if (currentSelectedFiles.size > 0) {
      const filenames = Array.from(currentSelectedFiles)
      bulkDelete.mutate({ projectId, filenames })
    }
  }

  // Auto-exit selection mode when selection becomes empty
  useEffect(() => {
    if (isSelectionMode && selectedCount === 0 && !isBulkDeleting) {
      setIsSelectionMode(false)
    }
  }, [isSelectionMode, selectedCount, isBulkDeleting])

  // Files that need attention: either missing from blob or not in vector store
  const hasLegacyFiles = files?.some((f) => f.needs_reupload === true)
  const hasFilesNotInVectorStore = files?.some(
    (f) =>
      f.vector_store_eligible !== false && f.in_vector_store === false && f.needs_reupload !== true
  )

  useEffect(() => {
    if (embeddingStatus?.is_complete && localStatus.pendingFileCount > 0) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectFiles(projectId) })
    }
  }, [embeddingStatus?.is_complete, localStatus.pendingFileCount, projectId, queryClient])

  const uploadFiles = useUploadProjectFiles()

  return (
    <>
      <div className="space-y-6">
        {canEdit && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium">{t("upload_files")}</h4>
              <p className="text-muted-foreground mt-1 text-xs">{t("upload_files_description")}</p>
            </div>

            <div className="space-y-3">
              <Button
                className="gap-2"
                disabled={uploadFiles.isPending || isBulkDeleting}
                variant="outline"
                onClick={() => setFilePickerOpen(true)}
              >
                <Upload className="h-4 w-4" />
                {t("add_files")}
                {uploadFiles.isPending && <Spinner className="h-3 w-3" />}
              </Button>
              <p className="text-muted-foreground text-xs">{t("supported_formats")}</p>
              <p className="text-muted-foreground text-xs">{t("image_note")}</p>
            </div>
          </div>
        )}

        {canEdit && <Separator />}

        <div className="space-y-4">
          <div>
            <div className="flex min-h-7 items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <h4 className="text-sm font-medium">{t("project_files")}</h4>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="text-muted-foreground h-3.5 w-3.5 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{t("project_files_info")}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {/* Selection mode toolbar - inline with title */}
              {canEdit && isSelectionMode && !isBulkDeleting ? (
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-foreground font-medium">
                    {selectedCount > 0
                      ? t("files_selected", { count: selectedCount })
                      : t("select_files_hint")}
                  </span>
                  <Button
                    className="h-6 px-2 text-xs"
                    disabled={selectableFilenamesList.length === 0}
                    size="sm"
                    variant="outline"
                    onClick={handleSelectAll}
                  >
                    {allSelected ? t("deselect_all") : t("select_all")}
                  </Button>
                  <Button
                    className="size-6 p-0"
                    disabled={selectedCount === 0}
                    size="sm"
                    variant="destructive"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                  <Button
                    className="size-6 p-0"
                    size="sm"
                    variant="ghost"
                    onClick={handleExitSelectionMode}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              ) : (
                /* Mobile select button - only visible on small screens when not in selection mode */
                canEdit &&
                files &&
                files.length > 0 &&
                selectableFilenamesList.length > 0 &&
                !isBulkDeleting && (
                  <Button
                    className="h-6 px-2 text-xs md:hidden"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEnterSelectionMode()}
                  >
                    <CheckSquare className="mr-1 h-3.5 w-3.5" />
                    {t("select")}
                  </Button>
                )
              )}
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
              {(() => {
                if (status.allFilesAreAnalysisReady) {
                  return status.fileCount === 1
                    ? t("analysis_ready_singular", { total: status.fileCount })
                    : t("analysis_ready_plural", { total: status.fileCount })
                }
                const indexedCount = status.indexedFileCount
                return indexedCount === 1
                  ? t("files_indexed_singular", { count: indexedCount })
                  : t("files_indexed_plural", { count: indexedCount })
              })()}
            </p>
            {/* Indexing progress indicator */}
            {hasPendingFiles && (
              <div className="text-muted-foreground mt-2 flex items-center gap-1.5 text-xs">
                <div className="h-2·w-2·animate-pulse-fast bg-primary rounded-full" />
                <span className="animate-shimmer from-muted-foreground via-foreground/50 to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-transparent">
                  {(() => {
                    const remaining = status.pendingFileCount
                    return remaining === 1
                      ? t("indexing_progress_singular", { remaining })
                      : t("indexing_progress_plural", { remaining })
                  })()}
                </span>
              </div>
            )}
            {/* Deletion progress indicator (matching indexing style) */}
            {deletionProgress && (
              <div className="text-muted-foreground mt-2 flex items-center gap-1.5 text-xs">
                <div className="h-2·w-2·animate-pulse-fast bg-destructive rounded-full" />
                <span className="animate-shimmer from-muted-foreground via-foreground/50 to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-transparent">
                  {t("removing_files", {
                    current: deletionProgress.completed,
                    total: deletionProgress.total,
                  })}
                </span>
              </div>
            )}
            {/* Legacy files warning banner - files missing from blob */}
            {hasLegacyFiles && (
              <div className="bg-destructive/10 mt-2 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs">
                <div className="bg-destructive h-2 w-2 rounded-full" />
                <span className="text-muted-foreground">{t("legacy_files_warning")}</span>
              </div>
            )}
            {/* Re-indexing in progress banner - backend eagerly re-indexes on project
                open (see routes/projects.py::list_project_files); useProjectFiles keeps
                polling until in_vector_store flips true. Gated on isReindexPolling (not
                just hasFilesNotInVectorStore) so it doesn't claim work is happening for
                readers (who never trigger the eager re-index) or after the poll cap. */}
            {hasFilesNotInVectorStore && !hasLegacyFiles && isReindexPolling && (
              <div className="bg-warning/15 border-warning mt-2 flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs">
                <div className="animate-pulse-fast bg-warning-ink h-2 w-2 rounded-full" />
                <span className="animate-shimmer from-muted-foreground via-foreground/50 to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-transparent">
                  {t("vector_store_expired_warning")}
                </span>
              </div>
            )}
          </div>

          <ProjectFileList
            embeddingStatus={activeEmbeddingStatus}
            files={files}
            isBulkDeleting={isBulkDeleting}
            isLoading={isListingFiles}
            isSelectionMode={isSelectionMode}
            projectId={projectId}
            selectableFilenames={selectableFilenamesSet}
            onEnterSelectionMode={handleEnterSelectionMode}
          />
        </div>
      </div>
      <FilePickerDialog
        open={filePickerOpen}
        source="recent"
        onOpenChange={setFilePickerOpen}
        onLocalFilesSelected={async (selected) => {
          await uploadFiles.mutateAsync({ projectId, files: selected })
        }}
        onSelect={async (item) => {
          // Download the Graph file and upload it to the project
          try {
            const blob = await downloadGraphFileApi(item, settingsService.token)
            const mime = item.mimeType || blob.type || "application/octet-stream"
            const file = new File([blob], item.name, { type: mime })
            await uploadFiles.mutateAsync({ projectId, files: [file] })
          } catch (error) {
            // Previously unhandled: a rejected Graph download (e.g. an encrypted
            // Office file) failed silently with nothing shown to the user.
            toast.error(graphDownloadErrorMessage(error))
          }
        }}
      />
    </>
  )
}
